import { env } from '../config/environment';

// API configuration using environment variables
const API_BASE_URL = env.apiBaseUrl;

export class ImageService {
  // Image Service Methods
  static async uploadImage(
    file: File, 
    containerName?: string, 
    blogPostId?: string
  ): Promise<{ success: boolean; imageUrl?: string; message: string }> {
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (containerName) formData.append('containerName', containerName);
      if (blogPostId) formData.append('blogPostId', blogPostId);

      const response = await fetch(`${API_BASE_URL}/images/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          imageUrl: data.imageUrl,
          message: 'Image uploaded successfully'
        };
      } else {
        return {
          success: false,
          message: data.error || 'Failed to upload image'
        };
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }

  static async uploadImageBase64(
    fileName: string,
    contentType: string,
    base64Data: string,
    containerName?: string,
    blogPostId?: string
  ): Promise<{ success: boolean; imageUrl?: string; message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/images/upload-base64`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName,
          contentType,
          base64Data,
          containerName,
          blogPostId
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          imageUrl: data.imageUrl,
          message: 'Image uploaded successfully'
        };
      } else {
        return {
          success: false,
          message: data.error || 'Failed to upload image'
        };
      }
    } catch (error) {
      console.error('Failed to upload base64 image:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }

  static async deleteImage(imageUrl: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/images?imageUrl=${encodeURIComponent(imageUrl)}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          message: 'Image deleted successfully'
        };
      } else {
        return {
          success: false,
          message: data.error || 'Failed to delete image'
        };
      }
    } catch (error) {
      console.error('Failed to delete image:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }

  static async listImages(
    containerName?: string, 
    prefix?: string
  ): Promise<{ success: boolean; images?: string[]; message: string }> {
    try {
      const params = new URLSearchParams();
      if (containerName) params.append('containerName', containerName);
      if (prefix) params.append('prefix', prefix);

      const response = await fetch(`${API_BASE_URL}/images?${params.toString()}`);
      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          images: data.images,
          message: 'Images retrieved successfully'
        };
      } else {
        return {
          success: false,
          message: data.error || 'Failed to retrieve images'
        };
      }
    } catch (error) {
      console.error('Failed to list images:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }

  static async getSecureImageUrl(
    blobName: string,
    containerName?: string,
    expiryHours?: number
  ): Promise<{ success: boolean; secureUrl?: string; message: string }> {
    try {
      const params = new URLSearchParams();
      if (expiryHours) params.append('expiryHours', expiryHours.toString());

      const response = await fetch(
        `${API_BASE_URL}/images/${containerName || 'blog-images'}/${blobName}/secure-url?${params.toString()}`
      );
      
      const data = await response.json();

      if (response.ok && data.success) {
        return {
          success: true,
          secureUrl: data.secureUrl,
          message: 'Secure URL generated successfully'
        };
      } else {
        return {
          success: false,
          message: data.error || 'Failed to generate secure URL'
        };
      }
    } catch (error) {
      console.error('Failed to get secure image URL:', error);
      return {
        success: false,
        message: 'Network error. Please try again later.'
      };
    }
  }
}